import type { MentionKind, MentionRef } from './types'

const UUID_PATTERN = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'
const UUID_RE = new RegExp(`^${UUID_PATTERN}$`, 'i')

// Global regex for splitting/matching mention tokens.
// Capture groups: (kind)(uuid)(display)
export const TOKEN_RE = new RegExp(
  `\\[\\[@(\\w+):(${UUID_PATTERN})\\|([^\\]]*?)\\]\\]`,
  'gi'
)

export type MentionToken = {
  raw: string
  kind: string
  id: string
  display: string
  index: number
}

export type ContentSegment =
  | { type: 'text'; value: string }
  | { type: 'mention'; kind: string; id: string; display: string }

/**
 * Split message content into text and mention segments.
 * Malformed tokens (bad UUID format) are left as plain text.
 */
export function parseContentSegments(text: string): ContentSegment[] {
  const segments: ContentSegment[] = []
  const re = new RegExp(TOKEN_RE.source, 'gi')
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = re.exec(text)) !== null) {
    const kind = match[1]
    const id = match[2]
    const display = match[3]

    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, match.index) })
    }

    if (UUID_RE.test(id)) {
      segments.push({ type: 'mention', kind, id, display })
    } else {
      // Malformed UUID — render as plain text
      segments.push({ type: 'text', value: match[0] })
    }

    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) })
  }

  return segments
}

/**
 * Extract only the mention refs from a message (for backend use).
 * Validates UUID format; ignores malformed tokens.
 *
 * Email guard: `@` in email addresses (e.g. user@domain.com) never produces
 * a token — tokens require the `[[@kind:uuid|display]]` wrapper.
 */
export function extractMentions(text: string): MentionRef[] {
  const refs: MentionRef[] = []
  const re = new RegExp(TOKEN_RE.source, 'gi')
  let match: RegExpExecArray | null
  const seen = new Set<string>()

  while ((match = re.exec(text)) !== null) {
    const kind = match[1] as MentionKind
    const id = match[2]
    if (!UUID_RE.test(id)) continue
    const key = `${kind}:${id}`
    if (seen.has(key)) continue
    seen.add(key)
    refs.push({ kind, id, display: match[3] })
  }

  return refs
}

/**
 * Serialize a resource into a mention token string.
 */
export function serializeMention(kind: MentionKind, id: string, display: string): string {
  return `[[@${kind}:${id}|${display}]]`
}

/**
 * Find the active @-trigger at or before the cursor position.
 * Returns null if no trigger, or if the @ is inside an email (preceded by a word char).
 *
 * Email guard example: `email me at user@domain.com` — the `@` is preceded by `r`
 * (a word char), so it does NOT trigger.
 *
 * Inline test cases:
 *   findMentionTrigger("@snap", 5)                      → { query: "snap", start: 0 }
 *   findMentionTrigger("hello @snap", 11)               → { query: "snap", start: 6 }
 *   findMentionTrigger("user@domain.com", 15)           → null (email, preceded by 'r')
 *   findMentionTrigger("send email to user@atrium.run", 29) → null (email)
 *   findMentionTrigger("hello @", 7)                    → { query: "", start: 6 }
 *   findMentionTrigger("/refresh @snap", 14)            → { query: "snap", start: 9 }
 */
export function findMentionTrigger(
  text: string,
  cursorPos: number
): { query: string; start: number } | null {
  const beforeCursor = text.slice(0, cursorPos)
  // @ preceded by start-of-string or whitespace, followed by word chars (or end)
  const match = beforeCursor.match(/(^|[\s])@(\w*)$/)
  if (!match) return null
  // match[1] is the preceding char (space or empty), match[2] is the query
  const atIndex = beforeCursor.lastIndexOf('@')
  return { query: match[2] ?? '', start: atIndex }
}
